import React, { useState } from 'react';
import { GoogleLogin, useGoogleLogin } from '@react-oauth/google';
import { useAuth } from '../contexts/AuthContext';
import { jwtDecode } from 'jwt-decode';
import './Auth.css';

const Auth = ({ onClose }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'El email es requerido';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'El email no es válido';
    }

    if (!formData.password) {
      newErrors.password = 'La contraseña es requerida';
    } else if (formData.password.length < 6) {
      newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
    }

    if (!isLogin) {
      if (!formData.name) {
        newErrors.name = 'El nombre es requerido';
      }
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Confirma tu contraseña';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Las contraseñas no coinciden';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      // Aquí iría la llamada a la API para login/registro
      // Por ahora simularemos una respuesta exitosa
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const userData = {
        id: Date.now(),
        name: formData.name || formData.email.split('@')[0],
        email: formData.email,
        provider: 'email'
      };
      
      login(userData);
      onClose && onClose();
    } catch (error) {
      setErrors({ general: 'Error al procesar la solicitud. Inténtalo de nuevo.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const decoded = jwtDecode(credentialResponse.credential);
      const userData = {
        id: decoded.sub,
        name: decoded.name,
        email: decoded.email,
        picture: decoded.picture,
        provider: 'google'
      };
      
      // Enviar datos al backend para guardar en la base de datos
      const response = await fetch('http://localhost:3003/api/auth/google', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: userData.id,
          name: userData.name,
          email: userData.email,
          picture: userData.picture,
          accessToken: credentialResponse.credential,
          refreshToken: null // Google OAuth no proporciona refresh token en este flujo
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        // Guardar información de sesión
        localStorage.setItem('sessionId', result.data.sessionId);
        localStorage.setItem('userId', result.data.userId);
        
        // Actualizar contexto de autenticación
        login({
          ...userData,
          sessionId: result.data.sessionId,
          userId: result.data.userId,
          isNewUser: result.data.isNewUser
        });
        
        console.log(result.data.isNewUser ? '✅ Usuario registrado exitosamente' : '✅ Login exitoso');
        onClose && onClose();
      } else {
        console.error('Error del servidor:', result.message);
        setErrors({ general: result.message || 'Error al procesar el login con Google' });
      }
    } catch (error) {
      console.error('Error al procesar login de Google:', error);
      setErrors({ general: 'Error al procesar el login con Google' });
    }
  };

  const handleGoogleError = () => {
    setErrors({ general: 'Error al conectar con Google. Inténtalo de nuevo.' });
  };

  // Hook alternativo para Google Login
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        // Obtener información del usuario usando el token de acceso
        const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
          headers: {
            Authorization: `Bearer ${tokenResponse.access_token}`,
          },
        });
        
        if (response.ok) {
          const userInfo = await response.json();
          const userData = {
            id: userInfo.id,
            name: userInfo.name,
            email: userInfo.email,
            picture: userInfo.picture,
            provider: 'google'
          };
          
          login(userData);
          onClose && onClose();
        } else {
          throw new Error('Failed to fetch user info');
        }
      } catch (error) {
        console.error('Error al procesar login de Google:', error);
        setErrors({ general: 'Error al procesar el login con Google' });
      }
    },
    onError: () => {
      setErrors({ general: 'Error al conectar con Google. Inténtalo de nuevo.' });
    },
    flow: 'implicit'
  });

  return (
    <div className="auth-container">
      <div className="auth-header">
        <h2>{isLogin ? 'Iniciar Sesión' : 'Registrarse'}</h2>
        <p className="auth-subtitle">
          {isLogin 
            ? 'Accede a tu cuenta de Checkin24' 
            : 'Crea tu cuenta en Checkin24'
          }
        </p>
      </div>

      {errors.general && (
        <div className="error-message general-error">
          {errors.general}
        </div>
      )}

      <form onSubmit={handleSubmit} className="auth-form">
        {!isLogin && (
          <div className="form-group">
            <label htmlFor="name">Nombre completo</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={errors.name ? 'error' : ''}
              placeholder="Tu nombre completo"
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className={errors.email ? 'error' : ''}
            placeholder="tu@email.com"
          />
          {errors.email && <span className="error-message">{errors.email}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="password">Contraseña</label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleInputChange}
            className={errors.password ? 'error' : ''}
            placeholder="Mínimo 6 caracteres"
          />
          {errors.password && <span className="error-message">{errors.password}</span>}
        </div>

        {!isLogin && (
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirmar contraseña</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className={errors.confirmPassword ? 'error' : ''}
              placeholder="Repite tu contraseña"
            />
            {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
          </div>
        )}

        <button 
          type="submit" 
          className="auth-submit-btn"
          disabled={isLoading}
        >
          {isLoading ? 'Procesando...' : (isLogin ? 'Iniciar Sesión' : 'Registrarse')}
        </button>
      </form>

      <div className="auth-divider">
        <span>o</span>
      </div>

      <div className="google-login-container">
        <button 
          type="button"
          className="google-login-btn"
          onClick={() => googleLogin()}
          disabled={isLoading}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" className="google-icon">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {isLogin ? 'Continuar con Google' : 'Registrarse con Google'}
        </button>
      </div>

      <div className="auth-switch">
        <p>
          {isLogin ? '¿No tienes cuenta?' : '¿Ya tienes cuenta?'}
          <button 
            type="button" 
            className="switch-btn"
            onClick={() => {
              setIsLogin(!isLogin);
              setErrors({});
              setFormData({
                email: '',
                password: '',
                name: '',
                confirmPassword: ''
              });
            }}
          >
            {isLogin ? 'Regístrate' : 'Inicia sesión'}
          </button>
        </p>
      </div>
    </div>
  );
};

export default Auth;